import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import {
  doCreateOrder,
  doDeleteOrder,
  doDeleteOrders,
  doGetOrder,
  doGetOrderByDBId,
  doGetOrders,
  doImportOrders,
  doRetryOrderAddress,
  doRetryOrder,
  doUpdateOrder,
  doUpdateOrdersBatch,
  doCancelOrder,
} from './thunk';
import {
  doGetInactiveOrders,
  doConfirmInactiveOrder,
  doPerformOrderAction,
  doGetIncorrectlyFailedOrders,
  doCreateClaim,
} from './thunkts';
import { ORDER_STATUSES } from '../../../constants';

const isActionWithPending = (action) => {
  return action.type && action.type.startsWith('orders/') && action.type.endsWith('/pending');
};

const isActionWithFulfilled = (action) => {
  return action.type && action.type.startsWith('orders/') && action.type.endsWith('/fulfilled');
};

const isActionWithFailed = (action) => {
  return action.type && action.type.startsWith('orders/') && action.type.endsWith('/rejected');
};

const ordersAdapter = createEntityAdapter({
  selectId: (item) => item.id,
});

const orderSlice = createSlice({
  name: 'order',
  initialState: {
    action: null,
    orderDetail: null,
    isFetching: false,
    status: 'idle',
    metadata: {},
    entities: [],
    inactiveOrders: [],
    incorrectlyFailedOrders: [],
    ids: [],
    item: null,
  },
  reducers: {
    clearState: (state) => {
      state.orderDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(doGetOrders.pending, (state) => {
      state.action = 'getall';
      state.status = 'loading';
    });
    builder.addCase(doGetOrders.fulfilled, (state, action) => {
      const { orders, pagination } = action.payload;
      state.status = 'succeeded';
      state.metadata = pagination;
      ordersAdapter.setAll(state, orders);
    });
    builder.addCase(doGetOrders.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doGetOrder.pending, (state) => {
      state.action = 'getOne';
      state.status = 'loading';
    });
    builder.addCase(doGetOrder.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.orderDetail = action.payload;
    });
    builder.addCase(doGetOrder.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doGetOrderByDBId.pending, (state) => {
      state.action = 'getOne';
      state.status = 'loading';
    });
    builder.addCase(doGetOrderByDBId.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.orderDetail = action.payload;
    });
    builder.addCase(doGetOrderByDBId.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doCreateOrder.pending, (state) => {
      state.action = 'create';
      state.status = 'loading';
    });
    builder.addCase(doCreateOrder.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doCreateOrder.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doImportOrders.pending, (state) => {
      state.action = 'import';
      state.status = 'loading';
    });
    builder.addCase(doImportOrders.fulfilled, (state, action) => {
      state.status = 'succeeded';
      ordersAdapter.addMany(state, action.meta.arg);
    });
    builder.addCase(doImportOrders.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doUpdateOrder.pending, (state) => {
      state.action = 'update';
      state.status = 'loading';
    });
    builder.addCase(doUpdateOrder.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doUpdateOrder.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doRetryOrderAddress.pending, (state) => {
      state.action = 'retry';
      state.status = 'loading';
    });
    builder.addCase(doRetryOrderAddress.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.order = action.payload;
      ordersAdapter.addOne(state, action.payload);
    });
    builder.addCase(doRetryOrderAddress.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doRetryOrder.pending, (state) => {
      state.action = 'retry';
      state.status = 'loading';
    });
    builder.addCase(doRetryOrder.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doRetryOrder.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doUpdateOrdersBatch.pending, (state) => {
      state.action = 'update';
      state.status = 'loading';
    });
    builder.addCase(doUpdateOrdersBatch.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.order = action.payload;
    });
    builder.addCase(doUpdateOrdersBatch.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doDeleteOrder.pending, (state) => {
      state.action = 'delete';
      state.status = 'loading';
    });
    builder.addCase(doDeleteOrder.fulfilled, (state, action) => {
      state.status = 'succeeded';
      ordersAdapter.removeOne(state, action.meta.arg);
    });
    builder.addCase(doDeleteOrder.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doCancelOrder.pending, (state) => {
      state.action = 'update';
      state.status = 'loading';
    });
    builder.addCase(doCancelOrder.fulfilled, (state) => {
      state.status = 'succeeded';
      state.order = { ...state.order, status: ORDER_STATUSES.CANCELLED };
    });
    builder.addCase(doCancelOrder.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doDeleteOrders.pending, (state) => {
      state.action = 'delete-multi';
      state.status = 'loading';
    });
    builder.addCase(doDeleteOrders.fulfilled, (state, action) => {
      state.status = 'succeeded';
      ordersAdapter.removeOne(state, action.meta.arg);
    });
    builder.addCase(doDeleteOrders.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doGetInactiveOrders.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(doGetInactiveOrders.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.inactiveOrders = action.payload?.orders;
    });
    builder.addCase(doGetInactiveOrders.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doGetIncorrectlyFailedOrders.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(doGetIncorrectlyFailedOrders.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.incorrectlyFailedOrders = action.payload?.orders;
    });
    builder.addCase(doGetIncorrectlyFailedOrders.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doConfirmInactiveOrder.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(doConfirmInactiveOrder.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.inactiveOrders = state.inactiveOrders.filter((order) => order.orderId !== action.meta.arg.orderId);
    });
    builder.addCase(doConfirmInactiveOrder.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doPerformOrderAction.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(doPerformOrderAction.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doPerformOrderAction.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doCreateClaim.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(doCreateClaim.fulfilled, (state) => {
      state.status = 'succeeded';
    });
    builder.addCase(doCreateClaim.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addMatcher(isActionWithPending, (state) => {
      state.isFetching = true;
      state.message = null;
    });
    builder.addMatcher(isActionWithFulfilled, (state) => {
      state.isFetching = false;
    });
    builder.addMatcher(isActionWithFailed, (state) => {
      state.isFetching = false;
      state.message = null;
    });
  },
});

export const {
  selectAll: selectAllOrders,
  selectById: selectOrderById,
  selectIds: selectOrderIds,
} = ordersAdapter.getSelectors((state) => state.order);

export const selectInactiveOrders = (state) => state.order.inactiveOrders ?? [];
export const selectIncorrectlyFailedOrders = (state) => state.order.incorrectlyFailedOrders ?? [];

const { actions, reducer: orderReducer } = orderSlice;
export const { clearState } = actions;
export default orderReducer;
